import React, { ReactNode, useEffect, useState } from "react";
import styled from "styled-components";
import { useQuery } from "@apollo/react-hooks";
import WarningIcon from "@material-ui/icons/Warning";
import { head, partition, some, sortBy, xor } from "lodash";
import { useHovered } from "@sonder/ui/source/hooks/useHovered";
import { CircleX } from "@sonder/ui/source/icons";
import { AvatarProps } from "@sonder/ui/source/styled/components/Avatar";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { FilterTextAreaWithClearButton } from "@sonder/ui/source/styled/components/FilterTextAreaWithClearButton";
import { MultiAvatar } from "@sonder/ui/source/styled/components/MultiAvatar";
import { ButtonProps } from "@sonder/ui/source/styled/fragments/Button";
import { CancelButton } from "@sonder/ui/source/styled/fragments/CancelButton";
import {
  FilterButtonList,
  FilterDropdownContent,
  FilterEmpty,
} from "@sonder/ui/source/styled/fragments/GenericFilters";
import { InputGroup } from "@sonder/ui/source/styled/fragments/InputGroup";
import { Label } from "@sonder/ui/source/styled/fragments/Label";
import { LabeledIconButton } from "@sonder/ui/source/styled/fragments/LabeledIconButton";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import { Stack } from "@sonder/ui/source/styled/fragments/Stack";
import { DropdownContentAlignment } from "@sonder/ui/source/styled/surfaces/DropdownContent";
import { borders, colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { useStore as useAuthStore } from "auth/store";
import { ENABLE_MULTI_SELECTION } from "core/constants";
import { useErrorReporterFiltered } from "core/hooks/useErrorReporterFiltered";
import { TagTypes, TomTags } from "core/queries/GetTags";
import {
  GetUsersResponse,
  GetUsersVariables,
  getUsers,
} from "core/queries/GetUsers";
import { useUpdateTask } from "core/queries/UpdateTask";
import { mapUserDataToProps } from "core/queries/mappers";
import { AssigneePickerList } from "tasks/components/AssigneePicker.List";
import { AssigneePickerGroupList } from "tasks/components/AssigneePickerGroup.List";
import { BatchedTasksType, GET_USER, TNS_USER } from "tasks/constants";
import { isCleanTask, isSpaceCleanTask, normalizeType } from "tasks/helpers";
import { useStore as useTaskStore } from "tasks/store";
import { TypeEnum } from "typings/globalGQLTypes";

const AssigneeWarning = styled.div`
  background: ${colors.white};
  border-top: ${borders.standard};
  bottom: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin: 0 -0.5rem -0.5rem;
  padding: 0.5rem 1.2rem;
  position: sticky;
  z-index: 1;
`;

const AssigneeInputGroup = styled(InputGroup)`
  overflow: hidden;
`;

const NameWrapper = styled.span`
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UnassignAll = styled.div`
  border-top: 1px solid ${colors.grayE};
  padding: 0.625rem;
  text-align: right;
`;

// Exports
interface RenderTriggerArgs {
  assignees: AvatarProps[];
  hasMultipleSupport: boolean;
  onClear?(): void;
}
interface AssigneePickerProps {
  alignContent?: DropdownContentAlignment;
  assigneeIds: string[];
  className?: string;
  companyIds?: string[];
  contentLoader?: ReactNode;
  disabled?: boolean;
  eventName: string;
  maxLength?: number;
  onAssign?(nextValue: string[] | string | null): void;
  refetchQueries?: string[];
  renderTrigger: (args: RenderTriggerArgs) => JSX.Element;
  tags?: TomTags[];
  taskCityId: string | undefined;
  taskId: string;
  taskType?: string;
  unassignCopy?: string;
}

interface AssigneePickerTriggerProps
  extends Pick<RenderTriggerArgs, "assignees" | "onClear">,
    ButtonProps {}

export const AssigneePickerTrigger = ({
  assignees = [],
  className,
  disabled,
  onClick,
  onClear,
}: AssigneePickerTriggerProps) => {
  const { hovered, onMouseEnter, onMouseLeave } = useHovered();
  const hasMultipleAssignees = assignees && assignees.length > 1;
  const firstAssigneeName = assignees[0] && assignees[0].name;
  const name = hasMultipleAssignees
    ? `${assignees.length} assignees`
    : firstAssigneeName;
  return (
    <LabeledIconButton
      className={className}
      disabled={disabled}
      title={
        assignees && assignees.length > 0
          ? `Assigned to: ${assignees
              .map(assignee => assignee.name)
              .join(", ")}`
          : "Unassigned"
      }
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <MultiAvatar avatars={assignees} />
      <AssigneeInputGroup>
        <Stack>
          {assignees && assignees.length > 0 && <Label>Assigned to</Label>}
          <NameWrapper>{name || "Unassigned"}</NameWrapper>
        </Stack>
        {name && hovered && onClear && (
          <CancelButton
            onClick={e => {
              onClear();
              e.stopPropagation();
            }}
          >
            <CircleX />
          </CancelButton>
        )}
      </AssigneeInputGroup>
    </LabeledIconButton>
  );
};

const getCityId = (
  user: User | null,
  filterCityId: string | null | undefined,
  taskCityId: string | undefined,
) => {
  return user && (user.isHKManager || user.isTOMMember)
    ? filterCityId
    : taskCityId;
};

const getDefaultUsers = (taskType?: string) => {
  const normalizedTaskType = taskType && normalizeType(taskType);
  switch (normalizedTaskType) {
    case BatchedTasksType:
      return [TNS_USER];
    case TypeEnum.Tom_Tasks_TrustAndSafetyFollowup:
    case TypeEnum.Tom_Tasks_HoldSecurityDeposit:
      return [TNS_USER];
    case TypeEnum.Tom_Tasks_GetFollowup:
      return [GET_USER];
    default:
      return [];
  }
};

const getMultipleSuppport = (taskType?: string) => {
  const normalizedTaskType = taskType && normalizeType(taskType);

  switch (normalizedTaskType) {
    case TypeEnum.Tom_Tasks_Clean: {
      return true;
    }
    default:
      return localStorage.getItem(ENABLE_MULTI_SELECTION) === "true";
  }
};

export function AssigneePicker({
  alignContent,
  assigneeIds = [],
  className,
  companyIds = [],
  contentLoader = null,
  disabled = false,
  eventName,
  maxLength = 10,
  onAssign,
  refetchQueries = ["GetTasks"],
  renderTrigger,
  taskCityId,
  taskId,
  tags = [],
  taskType,
  unassignCopy = "Unassign All",
}: AssigneePickerProps) {
  const {
    state: { user: loggedInUser },
  } = useAuthStore();
  const errorReporter = useErrorReporterFiltered();
  const {
    state: {
      filters: { city },
    },
  } = useTaskStore();
  const { track } = useSegment();
  const [search, setSearch] = useState("");
  const [newAssigneeIds, setNewAssigneeIds] = useState(assigneeIds);
  const hasMultipleSupport = getMultipleSuppport(taskType);
  const maxAssigneesReached = newAssigneeIds?.length >= maxLength;

  // If props.assigneeIds changes, we need to update the Picker
  useEffect(() => {
    setNewAssigneeIds(assigneeIds);
  }, [assigneeIds.join(",")]);
  const cityId = getCityId(loggedInUser, city, taskCityId);

  // If we're looking for users in a company, they will not neccesarily be part of any city.
  const variables =
    companyIds.length && isCleanTask(taskType ?? "")
      ? {
          filterCompanies: companyIds,
        }
      : {
          filterCity: cityId || "",
        };

  // Fetch team members / employees
  const { data, error, loading } = useQuery<
    GetUsersResponse,
    GetUsersVariables
  >(getUsers, {
    skip: !cityId,
    variables,
  });
  const users = sortBy(
    (data?.userConnections?.users || [])
      .concat(getDefaultUsers(taskType))
      .map(mapUserDataToProps),
    ["firstName"],
  );

  const isEmpty = users.length === 0;
  const isEmptyAndSearched = isEmpty && search !== "";

  // Assign team member to a task
  const [onUpdateTask] = useUpdateTask({
    refetchQueries,
  });

  let currentAssignees = users.filter(
    ({ id }) => !!newAssigneeIds?.includes(id),
  );
  const missingAssignees = newAssigneeIds.filter(
    id => !users.find(user => user.id === id),
  );

  // Sad path, we have assignee(s) in another city for this task, so currentAssignees is possibly empty.
  // Query a little bit more data so the AssigneePicker correctly shows these assignee(s).
  const {
    data: userData,
    error: userError,
    loading: userLoading,
  } = useQuery<GetUsersResponse, GetUsersVariables>(getUsers, {
    skip: loading || !cityId || !missingAssignees.length,
    variables: { ids: missingAssignees },
  });
  if (missingAssignees.length && userData && userData.userConnections?.users) {
    currentAssignees = currentAssignees.concat(
      userData.userConnections.users.map(user => mapUserDataToProps(user)),
    );
  }

  const anyError = userError || error;
  const anyLoading = userLoading || loading;

  const onHandleMultiAssign = (nextAssigneeIds: string[]) => {
    if (onAssign) {
      onAssign(nextAssigneeIds);
    }
    setNewAssigneeIds(nextAssigneeIds);
  };

  const onHandleAssign = async (nextAssigneeId: string) => {
    if (onAssign) {
      onAssign(nextAssigneeId);
    } else {
      try {
        await onUpdateTask({
          variables: {
            taskId,
            assigneeId: nextAssigneeId || "",
            // This "hacky" fix fixes some team related issues.
            ...(taskType && isSpaceCleanTask(taskType)
              ? { assigneeIds: [nextAssigneeId] }
              : {}),
          },
        });
      } catch (e) {
        errorReporter.notify(e);
        setNewAssigneeIds(newAssigneeIds);
      }
    }
  };

  const filteredUsers = users.filter(user => {
    return user.name.toLowerCase().startsWith(search.toLowerCase());
  });

  const squadTag = head(tags.filter(tag => tag.tagType === TagTypes.Squad));
  const squadTagName = squadTag && squadTag.name;

  const [usersWithMatchingSquad, otherUsers] = !squadTagName
    ? [[], filteredUsers]
    : partition(filteredUsers, user =>
        some(user.tags, { tagType: TagTypes.Squad, name: squadTagName }),
      );

  if (anyLoading) {
    if (!contentLoader) return null;
    // Annoyingly, `ReactNode` accepts `{}` as valid, so returning the `contentLoader` will cause a bunch of TS errors.
    // Any better solutions, ping me and yell!
    return <>{contentLoader}</>;
  }

  return anyError ? null : (
    <Dropdown
      className={className}
      trigger={renderTrigger({
        hasMultipleSupport,
        assignees: currentAssignees.map(assignee => ({
          imageUrl: assignee.avatarUrl,
          ...assignee,
        })),
        onClear: () => {
          track({
            eventName,
            action: "click",
            element: "assignee_selector.clear",
            props: { taskId },
          });
          if (!hasMultipleSupport) {
            onHandleAssign("");
          }
        },
      })}
      onClose={async () => {
        if (hasMultipleSupport) {
          try {
            const hasAssigneeIdsChanged =
              xor(assigneeIds, newAssigneeIds).length > 0;

            if (hasAssigneeIdsChanged) {
              await onUpdateTask({
                variables: {
                  assigneeIds: newAssigneeIds,
                  taskId,
                },
              });
            }

            track({
              action: "click",
              element: "assignee_selector.dropdown_trigger",
              eventName,
              props: { nextValue: "closed", taskId },
            });
          } catch (e) {
            errorReporter.notify(e);
            setNewAssigneeIds(newAssigneeIds);
          }
        }
      }}
      onOpen={() => {
        track({
          eventName,
          action: "click",
          element: "assignee_selector.dropdown_trigger",
          props: { nextValue: "opened", taskId },
        });
      }}
    >
      {({ isOpen, closePortal }) => {
        const AssigneePickerListComponent =
          usersWithMatchingSquad.length > 0
            ? AssigneePickerGroupList
            : AssigneePickerList;

        return (
          isOpen && (
            <FilterDropdownContent
              align={alignContent}
              data-testid="assignee-picker-dropdown-content"
            >
              <FilterTextAreaWithClearButton
                autoFocus
                disabled={loading || !!error}
                placeholder={`Search ${users.length} ${
                  users.length > 1 ? "assignees" : "assignee"
                }`}
                value={search}
                onChange={e => setSearch(e.target.value)}
                onClear={() => {
                  track({
                    eventName,
                    action: "clear",
                    element: "assignee_selector.search_input",
                    props: { taskId },
                  });
                  setSearch("");
                }}
                onFocus={() => {
                  track({
                    eventName,
                    action: "focus",
                    element: "assignee_selector.search_input",
                    props: { taskId },
                  });
                }}
              />

              {isEmptyAndSearched && (
                <FilterEmpty>{`No Results for ${search}.`}</FilterEmpty>
              )}

              <FilterButtonList>
                <AssigneePickerListComponent
                  assigneeIds={newAssigneeIds || []}
                  hasMultipleSupport={hasMultipleSupport}
                  isDisabled={maxAssigneesReached || disabled}
                  squadTagName={squadTag && squadTag.name}
                  users={otherUsers}
                  usersWithMatchingSquad={usersWithMatchingSquad}
                  onSelect={({ itemId, nextAssigneeId }) => {
                    if (hasMultipleSupport) {
                      const nextAssigneeIds = xor(newAssigneeIds, [itemId]);
                      onHandleMultiAssign(nextAssigneeIds);
                    } else {
                      onHandleAssign(nextAssigneeId || "");
                      setSearch("");
                      closePortal();
                    }
                    track({
                      eventName,
                      action: "click",
                      element: "assignee_selector.user_card",
                      props: {
                        cardUserId: itemId,
                        nextAssigneeId,
                        taskId,
                      },
                    });
                  }}
                />
              </FilterButtonList>

              {hasMultipleSupport && !disabled && newAssigneeIds.length > 0 && (
                <UnassignAll>
                  <Link
                    onClick={() => {
                      requestAnimationFrame(() => onHandleMultiAssign([]));
                    }}
                  >
                    {unassignCopy}
                  </Link>
                </UnassignAll>
              )}

              {maxAssigneesReached && (
                <AssigneeWarning>
                  <WarningIcon /> Max of {maxLength} assignees.
                </AssigneeWarning>
              )}
            </FilterDropdownContent>
          )
        );
      }}
    </Dropdown>
  );
}
